import { enroll } from '@api/selfServiceServices';
import { type AppError, FeatureFlagsProvider, GlobalEventSources } from '@imprivata-cloud/core';
import { Button, NotificationSystem } from '@imprivata-cloud/design-system';
import {
  DefaultFaceCaptureBanner,
  FaceCaptureEnroll,
  FaceProcessingStatus,
} from '@imprivata-cloud/enroll';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelfServiceLandingPageDataResponse, UserInfo } from '@/api/types';

interface EnrollNotificationPayload {
  online: boolean;
  status: FaceProcessingStatus;
}

interface EnrollFlowProps {
  userInfo: UserInfo | undefined;
  data: SelfServiceLandingPageDataResponse | undefined;
  onSuccessfulFaceEnrollment: () => Promise<undefined | AppError>;
}
export const EnrollFlow = ({ userInfo, onSuccessfulFaceEnrollment, data }: EnrollFlowProps) => {
  const { t } = useTranslation();
  const [key, setKey] = useState(0);

  const forceUpdateComponent = useCallback(() => {
    setKey(key => key + 1);
  }, []);

  const translate = useCallback((i18nKey: string) => t(i18nKey), [t]);

  return (
    <FeatureFlagsProvider flags={{ fitVideoFrame: true }}>
      <NotificationSystem
        renderCustomBanner={event => {
          const payload = event?.payload as EnrollNotificationPayload;
          return (
            <DefaultFaceCaptureBanner
              type={event?.level ? event.level : 'info'}
              title={event?.title}
              message={event?.message}
              overlayActions={
                payload?.status === FaceProcessingStatus.FAILED ? (
                  <Button
                    variant="link"
                    disabled={!payload?.online}
                    onClick={forceUpdateComponent}
                  >
                    {t('common.try-again')}
                  </Button>
                ) : undefined
              }
            />
          );
        }}
      />
      <GlobalEventSources translate={translate} />
      <FaceCaptureEnroll.Root
        key={key}
        username={data?.userInfo.username || userInfo?.userUpn || userInfo?.email || ''}
        reset={forceUpdateComponent}
        enroll={enroll}
        handleEnrollComplete={onSuccessfulFaceEnrollment}
        isSuccessHandlerAutomatic={true}
      >
        <FaceCaptureEnroll.Overlay>
          <FaceCaptureEnroll.Camera />
        </FaceCaptureEnroll.Overlay>
      </FaceCaptureEnroll.Root>
    </FeatureFlagsProvider>
  );
};
